import { useState, useEffect, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import { Textarea } from "../components/ui/textarea";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "../components/ui/dropdown-menu";
import { Checkbox } from "../components/ui/checkbox";
import {
  Inbox,
  Send,
  Loader2,
  CheckCircle2,
  MessageCircle,
  Phone,
  PhoneCall,
  PhoneOutgoing,
  Clock,
  User,
  Search,
  Filter,
  Check,
  CheckCheck,
  AlertTriangle,
  AlertCircle,
  RefreshCw,
  ArrowLeft,
  UserPlus,
  Building2,
  Users,
  Plus,
  Zap,
  MessageSquarePlus,
  History,
  FileText,
  MoreVertical,
  Eye,
  EyeOff,
  Trash2,
  Paperclip,
  Download
} from "lucide-react";

const formatBytes = (bytes) => {
  if (bytes === null || bytes === undefined) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const InboundMediaMessage = ({ msg, getAuthHeaders }) => {
  const [imgUrl, setImgUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [size, setSize] = useState(msg.media_size ?? null);
  const isImage = msg.message_type === "image" || (msg.media_mime || "").startsWith("image/");

  const fetchBlob = async () => {
    const res = await axios.get(`${API}/whatsapp/media/${msg.id}`, { ...getAuthHeaders(), responseType: "blob" });
    return res.data;
  };

  useEffect(() => {
    let objUrl;
    (async () => {
      try {
        if (isImage) {
          setLoading(true);
          const blob = await fetchBlob();
          objUrl = URL.createObjectURL(blob);
          setImgUrl(objUrl);
          setSize(blob.size);
        } else if (size === null) {
          const res = await axios.get(`${API}/whatsapp/media/${msg.id}/info`, getAuthHeaders());
          if (res.data?.file_size != null) setSize(res.data.file_size);
        }
      } catch (e) {
        console.error("Error loading media:", e);
      } finally {
        setLoading(false);
      }
    })();
    return () => { if (objUrl) URL.revokeObjectURL(objUrl); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [msg.id]);

  const download = async () => {
    try {
      const blob = await fetchBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = msg.media_filename || "archivo";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      toast.error("No se pudo descargar el archivo");
    }
  };

  if (isImage) {
    return (
      <div className="py-1">
        {loading && <div className="text-xs opacity-70">Cargando imagen…</div>}
        {imgUrl && (
          <>
            <img
              src={imgUrl}
              alt={msg.media_filename || "imagen"}
              className="rounded-lg max-w-full max-h-64 cursor-pointer"
              onClick={() => window.open(imgUrl, "_blank")}
            />
            <div className="flex items-center gap-2 mt-1">
              <button onClick={download} className="flex items-center gap-1 text-xs hover:underline" data-testid={`download-media-${msg.id}`}>
                <Download className="w-3.5 h-3.5" /> Descargar
              </button>
              {size != null && <span className="text-xs opacity-70">{formatBytes(size)}</span>}
            </div>
          </>
        )}
        {msg.content && <p className="text-sm mt-1 break-words whitespace-pre-wrap">{msg.content}</p>}
      </div>
    );
  }

  return (
    <button onClick={download} className="flex items-center gap-2 py-1 text-left hover:underline" data-testid={`download-media-${msg.id}`}>
      <div className="w-8 h-8 rounded-lg bg-slate-100 dark:bg-slate-700 flex items-center justify-center flex-shrink-0">
        <Paperclip className="w-4 h-4" />
      </div>
      <span className="flex flex-col">
        <span className="break-words font-medium">{msg.media_filename || msg.content || "Descargar archivo"}</span>
        {size != null && <span className="text-xs opacity-70">{formatBytes(size)}</span>}
      </span>
    </button>
  );
};


export default function WhatsAppBandejaPage() {
  const [conversations, setConversations] = useState([]);
  const [showHidden, setShowHidden] = useState(false);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendingEncuesta, setSendingEncuesta] = useState(false);
  const [newMessage, setNewMessage] = useState("");
  const [quickReplies, setQuickReplies] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [soloReabiertas, setSoloReabiertas] = useState(false);
  const [filterEjecutivo, setFilterEjecutivo] = useState("");
  const [filterDepartamento, setFilterDepartamento] = useState("");
  const [filtrosOpts, setFiltrosOpts] = useState(null);
  const [reabiertasCount, setReabiertasCount] = useState(0);
  const [stats, setStats] = useState({});
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [assignmentData, setAssignmentData] = useState({ assigned_to: "", department_id: "", notes: "" });
  const [selectedColaborador, setSelectedColaborador] = useState(null);
  const [colaboradorSearch, setColaboradorSearch] = useState("");
  const [usuarios, setUsuarios] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [assigning, setAssigning] = useState(false);
  const [agentSearch, setAgentSearch] = useState("");
  const [listWidth, setListWidth] = useState(() => {
    const saved = parseInt(localStorage.getItem("wa_list_width") || "", 10);
    return Number.isFinite(saved) && saved >= 280 && saved <= 640 ? saved : 384;
  });
  const isResizingRef = useRef(false);
  const listWidthRef = useRef(384);
  
  // New conversation states
  const [showNewConversationDialog, setShowNewConversationDialog] = useState(false);
  const [newConvTab, setNewConvTab] = useState("manual"); // "manual" or "contacts"
  const [newConvPhone, setNewConvPhone] = useState("");
  const [newConvMessage, setNewConvMessage] = useState("");
  const [newConvConfigId, setNewConvConfigId] = useState("");
  const [startingConversation, setStartingConversation] = useState(false);
  const [waContacts, setWaContacts] = useState([]);
  const [contactSearch, setContactSearch] = useState("");
  const [selectedContact, setSelectedContact] = useState(null);
  const [loadingContacts, setLoadingContacts] = useState(false);
  
  // Call states
  const [callsEnabled, setCallsEnabled] = useState(false);
  const [callHistory, setCallHistory] = useState([]);
  const [showCallHistoryDialog, setShowCallHistoryDialog] = useState(false);
  const [loadingCallHistory, setLoadingCallHistory] = useState(false);

  // Approved template states
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [syncingTemplates, setSyncingTemplates] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateVars, setTemplateVars] = useState([]);
  const [templateTarget, setTemplateTarget] = useState(null); // { configId, phone }
  const [sendingTemplate, setSendingTemplate] = useState(false);
  
  const messagesEndRef = useRef(null);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [pastedImage, setPastedImage] = useState(null); // {file, url} imagen pegada del portapapeles o archivo soltado
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const fileInputRef = useRef(null);
  const { getAuthHeaders, user } = useAuth();
  const rolQR = (user?.role || "").toLowerCase();
  const esAdminQR = rolQR === "admin" || rolQR === "administrador";
  const deptoUsuarioQR = (user?.departamento_nombre || user?.department || "").toLowerCase();
  const visibleQuickReplies = quickReplies.filter(
    (q) => !q.department_name || esAdminQR ||
      (q.department_id && user?.departamento_id && q.department_id === user.departamento_id) ||
      (q.department_name || "").toLowerCase() === deptoUsuarioQR
  );

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const fetchConversations = useCallback(async (showLoader = false) => {
    try {
      if (showLoader) setRefreshing(true);
      
      const [convRes, configRes, statsRes, usersRes, deptRes, reaRes] = await Promise.all([
        axios.get(`${API}/whatsapp/conversations?limit=100&show_hidden=${showHidden}${filterEjecutivo ? `&assigned_to=${filterEjecutivo}` : ""}${filterDepartamento ? `&department_id=${filterDepartamento}` : ""}`, getAuthHeaders()),
        axios.get(`${API}/whatsapp/config`, getAuthHeaders()),
        axios.get(`${API}/whatsapp/stats`, getAuthHeaders()),
        axios.get(`${API}/users`, getAuthHeaders()),
        axios.get(`${API}/departamentos`, getAuthHeaders()),
        axios.get(`${API}/whatsapp/conversations/reabiertas/count`, getAuthHeaders())
      ]);
      setConversations(convRes.data?.conversations || []);
      setConfigs(configRes.data || []);
      setStats(statsRes.data || {});
      setUsuarios(usersRes.data || []);
      setDepartamentos(deptRes.data || []);
      setReabiertasCount(reaRes.data?.count || 0);
      
      // Check if any config has calls enabled
      const anyCallsEnabled = (configRes.data || []).some(c => c.calls_enabled);
      setCallsEnabled(anyCallsEnabled);
    } catch (err) {
      console.error("Error:", err);
      if (showLoader) toast.error("Error al actualizar");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [getAuthHeaders, showHidden, filterEjecutivo, filterDepartamento]);

  useEffect(() => {
    axios.get(`${API}/whatsapp/conversations/filtros`, getAuthHeaders())
      .then((r) => setFiltrosOpts(r.data))
      .catch(() => setFiltrosOpts({ mostrar: false, ejecutivos: [], departamentos: [] }));
  }, [getAuthHeaders]);

  // Abrir conversación específica desde una notificación (?conv=ID)
  const location = useLocation();
  const convParamProcessedRef = useRef(null);
  useEffect(() => {
    const convId = new URLSearchParams(location.search).get("conv");
    if (!convId || convParamProcessedRef.current === location.search || conversations.length === 0) return;
    convParamProcessedRef.current = location.search;
    const conv = conversations.find(c => c.id === convId);
    if (conv) {
      loadMessages(conv);
    } else {
      toast.info("La conversación no está visible en tu bandeja actual");
    }
    window.history.replaceState({}, "", "/whatsapp/bandeja");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, conversations]);

  // Manual refresh function
  const handleManualRefresh = () => {
    fetchConversations(true);
    if (selectedConversation) {
      loadMessages(selectedConversation);
    }
    toast.success("Actualizado");
  };

  const hideConversation = async (conv, e) => {
    e?.stopPropagation();
    try {
      const res = await axios.post(`${API}/whatsapp/conversations/${conv.id}/hide`, {}, getAuthHeaders());
      toast.success(res.data.hidden ? "Conversación ocultada" : "Conversación visible de nuevo");
      if (selectedConversation?.id === conv.id && res.data.hidden && !showHidden) {
        setSelectedConversation(null);
      }
      fetchConversations();
    } catch (err) {
      console.error("Error:", err);
      toast.error(err.response?.data?.detail || "Error al ocultar la conversación");
    }
  };

  const deleteConversation = async (conv, e) => {
    e?.stopPropagation();
    if (!window.confirm(`¿Eliminar definitivamente la conversación con ${conv.contact_name || conv.contact_phone}? Se borrarán todos sus mensajes. Esta acción no se puede deshacer.`)) {
      return;
    }
    try {
      await axios.delete(`${API}/whatsapp/conversations/${conv.id}`, getAuthHeaders());
      toast.success("Conversación eliminada");
      if (selectedConversation?.id === conv.id) setSelectedConversation(null);
      fetchConversations();
    } catch (err) {
      console.error("Error:", err);
      toast.error(err.response?.data?.detail || "Error al eliminar la conversación");
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const exitSelectionMode = () => {
    setSelectionMode(false);
    setSelectedIds([]);
  };

  const bulkDeleteConversations = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`¿Eliminar definitivamente ${selectedIds.length} conversación(es) y todos sus mensajes? Esta acción no se puede deshacer.`)) {
      return;
    }
    try {
      const res = await axios.post(`${API}/whatsapp/conversations/bulk-delete`, { conversation_ids: selectedIds }, getAuthHeaders());
      toast.success(`${res.data.deleted} conversación(es) eliminada(s)`);
      if (selectedConversation && selectedIds.includes(selectedConversation.id)) setSelectedConversation(null);
      exitSelectionMode();
      fetchConversations();
    } catch (err) {
      console.error("Error:", err);
      toast.error(err.response?.data?.detail || "Error al eliminar las conversaciones");
    }
  };

  useEffect(() => {
    fetchConversations();
    // Poll for new conversations every 5 seconds
    const interval = setInterval(fetchConversations, 5000);
    return () => clearInterval(interval);
  }, [fetchConversations]);

  useEffect(() => {
    axios.get(`${API}/whatsapp/quick-replies`, getAuthHeaders())
      .then((res) => setQuickReplies((res.data || []).filter((q) => q.is_active)))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-refresh messages every 3 seconds when viewing a conversation
  useEffect(() => {
    if (!selectedConversation) return;
    
    const refreshMessages = async () => {
      try {
        const res = await axios.get(`${API}/whatsapp/conversations/${selectedConversation.id}`, getAuthHeaders());
        const newMessages = res.data?.messages || [];
        
        // Only update if there are new messages to avoid unnecessary re-renders
        if (newMessages.length !== messages.length) {
          setMessages(newMessages);
          // Also refresh conversations to update unread counts
          fetchConversations();
        }
      } catch (err) {
        console.error("Error refreshing messages:", err);
      }
    };

    // Refresh every 3 seconds
    const messageInterval = setInterval(refreshMessages, 3000);
    return () => clearInterval(messageInterval);
  }, [selectedConversation, messages.length, getAuthHeaders, fetchConversations]);

  const loadMessages = async (conversation) => {
    try {
      setLoadingMessages(true);
      setSelectedConversation(conversation);
      const res = await axios.get(`${API}/whatsapp/conversations/${conversation.id}`, getAuthHeaders());
      setMessages(res.data?.messages || []);
      const telServidor = res.data?.conversation?.contact_phone ?? null;
      if (telServidor !== (conversation.contact_phone ?? null)) {
        setSelectedConversation({ ...conversation, contact_phone: telServidor });
      }
      
      // Mark as read
      if (conversation.unread_count > 0) {
        await axios.post(`${API}/whatsapp/conversations/${conversation.id}/read`, {}, getAuthHeaders());
        fetchConversations();
      }
    } catch (err) {
      console.error("Error:", err);
      toast.error("Error al cargar mensajes");
    } finally {
      setLoadingMessages(false);
    }
  };

  const markUnread = async (conv, e) => {
    e?.stopPropagation();
    try {
      await axios.post(`${API}/whatsapp/conversations/${conv.id}/unread`, {}, getAuthHeaders());
      if (selectedConversation?.id === conv.id) {
        setSelectedConversation(null);
        setMessages([]);
      }
      toast.success("Conversación marcada como no leída");
      fetchConversations();
    } catch {
      toast.error("Error al marcar como no leída");
    }
  };

  const fetchTemplates = async (configId) => {
    if (!configId) return;
    try {
      setLoadingTemplates(true);
      const res = await axios.get(`${API}/whatsapp/templates?config_id=${configId}`, getAuthHeaders());
      setTemplates(res.data || []);
    } catch (err) {
      console.error("Error:", err);
      toast.error("Error al cargar plantillas");
    } finally {
      setLoadingTemplates(false);
    }
  };

  const syncTemplates = async () => {
    const configId = templateTarget?.configId;
    if (!configId) {
      toast.error("Seleccione una línea de WhatsApp primero");
      return;
    }
    try {
      setSyncingTemplates(true);
      const res = await axios.post(`${API}/whatsapp/templates/sync?config_id=${configId}`, {}, getAuthHeaders());
      const delMsg = res.data.deleted > 0 ? `, ${res.data.deleted} eliminada(s) (ya no existen en Meta)` : "";
      toast.success(`${res.data.count} plantilla(s) sincronizada(s) desde Meta${delMsg}`);
      await fetchTemplates(configId);
    } catch (err) {
      console.error("Error:", err);
      toast.error(err.response?.data?.detail || "Error al sincronizar plantillas");
    } finally {
      setSyncingTemplates(false);
    }
  };

  const openTemplateDialog = (target) => {
    if (!target?.configId) {
      toast.error("Seleccione una línea de WhatsApp");
      return;
    }
    setTemplateTarget(target);
    setSelectedTemplate(null);
    setTemplateVars([]);
    setShowTemplateDialog(true);
    fetchTemplates(target.configId);
  };

  const handleSelectTemplate = (tpl) => {
    // Detecta variables del cuerpo Y del encabezado (numeradas o con nombre)
    const bodyFound = [...new Set(((tpl.body_text || "").match(/\{\{\s*[^{}]+?\s*\}\}/g) || []).map((m) => m.replace(/[{}]/g, "").trim()))];
    const headerCount = Math.max(tpl.header_var_count || 0, ((tpl.header_text || "").match(/\{\{\s*[^{}]+?\s*\}\}/g) || []).length);
    let named = tpl.variable_names || [];
    if (named.length === 0 && headerCount === 0 && bodyFound.some((f) => !/^\d+$/.test(f))) {
      named = bodyFound;
    }
    const count = Math.max(tpl.variable_count || 0, headerCount + bodyFound.length, headerCount + named.length);
    setSelectedTemplate({ ...tpl, variable_names: named, header_var_count: headerCount });
    setTemplateVars(Array(count).fill(""));
  };

  const templateVarsIncompletas = templateVars.some((v) => !v.trim());

  const renderTemplateText = (tpl, vars) => {
    if (!tpl) return "";
    const hdrN = tpl.header_var_count || 0;
    let header = tpl.header_text || "";
    (vars || []).slice(0, hdrN).forEach((v) => {
      header = header.replace(/\{\{\s*[^{}]+?\s*\}\}/, v || "$&");
    });
    let text = tpl.body_text || "";
    const named = tpl.variable_names || [];
    (vars || []).slice(hdrN).forEach((v, i) => {
      if (named.length > 0) {
        text = text.replaceAll(`{{${named[i]}}}`, v || `{{${named[i]}}}`);
      } else {
        text = text.replaceAll(`{{${i + 1}}}`, v || `{{${i + 1}}}`);
      }
    });
    return header ? `${header}\n${text}` : text;
  };

  const sendTemplate = async () => {
    if (!selectedTemplate || !templateTarget?.configId || !templateTarget?.phone) {
      toast.error("Faltan datos para enviar la plantilla");
      return;
    }
    if (templateVars.some((v) => !v.trim())) {
      toast.error("Debe completar todas las variables de la plantilla antes de enviarla");
      return;
    }
    try {
      setSendingTemplate(true);
      let cleanPhone = templateTarget.phone.replace(/[\s\-()]/g, "");
      if (!cleanPhone.startsWith("+")) cleanPhone = "+" + cleanPhone;
      await axios.post(`${API}/whatsapp/send`, {
        config_id: templateTarget.configId,
        recipient_phone: cleanPhone,
        template_name: selectedTemplate.name,
        template_language: selectedTemplate.language || "es",
        template_params: templateVars,
        ...((selectedTemplate.variable_names || []).length > 0 ? { template_param_names: selectedTemplate.variable_names } : {}),
        message_text: renderTemplateText(selectedTemplate, templateVars)
      }, getAuthHeaders());
      toast.success("Plantilla enviada");
      setShowTemplateDialog(false);
      setShowNewConversationDialog(false);
      if (selectedConversation && selectedConversation.contact_phone === cleanPhone) {
        loadMessages(selectedConversation);
      }
      setTimeout(() => fetchConversations(), 1500);
    } catch (err) {
      console.error("Error:", err);
      toast.error(err.response?.data?.detail || "Error al enviar plantilla");
    } finally {
      setSendingTemplate(false);
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const errorDetalle = (err, fallback) => {
    const d = err.response?.data?.detail;
    return typeof d === "string" ? d : fallback;
  };

  const definirTelefono = async () => {
    const valor = window.prompt("Número de WhatsApp del cliente (formato internacional, ej. 18095551234):");
    if (!valor) return;
    try {
      const res = await axios.put(`${API}/whatsapp/conversations/${selectedConversation.id}/contact-phone`, { phone: valor }, getAuthHeaders());
      setSelectedConversation({ ...selectedConversation, contact_phone: res.data.contact_phone });
      fetchConversations();
      toast.success(`Número guardado: ${res.data.contact_phone}`);
    } catch (err) {
      toast.error(errorDetalle(err, "No se pudo guardar el número"));
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConversation) return;

    // Chat interno: el mensaje no sale por WhatsApp, se guarda en la bandeja
    if (selectedConversation.tipo === "interna") {
      try {
        setSending(true);
        await axios.post(`${API}/whatsapp/interna/${selectedConversation.id}/mensaje`, { message_text: newMessage }, getAuthHeaders());
        setNewMessage("");
        loadMessages(selectedConversation);
      } catch (err) {
        toast.error(errorDetalle(err, "Error al enviar mensaje interno"));
      } finally {
        setSending(false);
      }
      return;
    }

    if (!selectedConversation.contact_phone) {
      toast.error("Esta conversación no tiene número de teléfono del cliente; no se puede enviar el mensaje.");
      return;
    }

    try {
      setSending(true);
      await axios.post(`${API}/whatsapp/send`, {
        config_id: selectedConversation.config_id,
        recipient_phone: selectedConversation.contact_phone,
        message_text: newMessage
      }, getAuthHeaders());
      
      setNewMessage("");
      if (selectedConversation.concluida) {
        setSelectedConversation({ ...selectedConversation, concluida: false, reabierta: true });
        fetchConversations();
      }
      // Reload messages
      loadMessages(selectedConversation);
      toast.success("Mensaje enviado");
    } catch (err) {
      console.error("Error:", err);
      toast.error(errorDetalle(err, "Error al enviar mensaje"));
    } finally {
      setSending(false);
    }
  };

  const handlePaste = (e) => {
    const items = e.clipboardData?.items || [];
    for (const item of items) {
      if (item.type && item.type.startsWith("image/")) {
        const blob = item.getAsFile();
        if (blob) {
          e.preventDefault();
          const ext = (item.type.split("/")[1] || "png").replace("jpeg", "jpg");
          const file = new File([blob], `captura_${Date.now()}.${ext}`, { type: item.type });
          setPastedImage({ file, url: URL.createObjectURL(file) });
        }
        return;
      }
    }
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    setIsDraggingFile(false);
    if (!selectedConversation) return;
    const file = e.dataTransfer?.files?.[0];
    if (!file) return;
    const url = (file.type || "").startsWith("image/") ? URL.createObjectURL(file) : null;
    setPastedImage({ file, url });
  };

  const sendPastedImage = async () => {
    if (!pastedImage) return;
    const img = pastedImage;
    setPastedImage(null);
    await sendAttachment([img.file]);
    if (img.url) URL.revokeObjectURL(img.url);
  };

  const sendAttachment = async (fileList) => {
    const file = fileList && fileList[0];
    if (!file || !selectedConversation) return;

    // Chat interno: el archivo se guarda en la plataforma, no sale por WhatsApp
    if (selectedConversation.tipo === "interna") {
      if (file.size > 50 * 1024 * 1024) {
        toast.error("El archivo supera el límite de 50 MB del chat interno");
        return;
      }
      try {
        setUploadingMedia(true);
        const formData = new FormData();
        if (newMessage.trim()) formData.append("caption", newMessage.trim());
        formData.append("file", file);
        await axios.post(`${API}/whatsapp/interna/${selectedConversation.id}/archivo`, formData, getAuthHeaders());
        setNewMessage("");
        loadMessages(selectedConversation);
        toast.success("Archivo enviado");
      } catch (err) {
        console.error("Error:", err);
        toast.error(errorDetalle(err, "Error al enviar el archivo"));
      } finally {
        setUploadingMedia(false);
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
      return;
    }

    if (!selectedConversation.contact_phone) {
      toast.error("Esta conversación no tiene número de teléfono del cliente; no se puede enviar el archivo.");
      return;
    }
    if (file.size > 100 * 1024 * 1024) {
      toast.error("El archivo supera el límite de 100 MB de WhatsApp");
      return;
    }
    try {
      setUploadingMedia(true);
      const formData = new FormData();
      formData.append("config_id", selectedConversation.config_id);
      formData.append("recipient_phone", selectedConversation.contact_phone);
      if (newMessage.trim()) formData.append("caption", newMessage.trim());
      formData.append("file", file);
      await axios.post(`${API}/whatsapp/send-media`, formData, getAuthHeaders());
      setNewMessage("");
      if (selectedConversation.concluida) {
        setSelectedConversation({ ...selectedConversation, concluida: false, reabierta: true });
        fetchConversations();
      }
      loadMessages(selectedConversation);
      toast.success("Archivo enviado");
    } catch (err) {
      console.error("Error:", err);
      toast.error(errorDetalle(err, "Error al enviar el archivo"));
    } finally {
      setUploadingMedia(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const getConfigName = (configId) => {
    const config = configs.find(c => c.id === configId);
    return config?.name || "Desconocido";
  };

  const isAdmin = ["admin", "administrador"].includes((user?.role || "").toLowerCase());

  const getDeptName = (deptId) => {
    if (!deptId) return null;
    return departamentos.find(d => d.id === deptId)?.name || null;
  };

  const getAgentName = (agentId) => {
    if (!agentId) return null;
    const u = usuarios.find(x => x.id === agentId);
    return u?.name || u?.email || null;
  };

  const isWindowClosed = (conv) => {
    if (!conv) return false;
    const ref = conv.last_inbound_at || conv.last_message_at;
    if (!ref) return true;
    const diffMs = Date.now() - new Date(ref).getTime();
    return diffMs > 24 * 60 * 60 * 1000;
  };

  const availableAgents = usuarios.filter(u => {
    if (assignmentData.department_id && u.departamento_id !== assignmentData.department_id) return false;
    if (agentSearch) {
      const s = agentSearch.toLowerCase();
      return (u.name || "").toLowerCase().includes(s) || (u.email || "").toLowerCase().includes(s);
    }
    return true;
  });

  const startResize = (e) => {
    e.preventDefault();
    isResizingRef.current = true;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    const onMove = (ev) => {
      if (!isResizingRef.current) return;
      const container = document.getElementById("wa-bandeja-body");
      if (!container) return;
      const left = container.getBoundingClientRect().left;
      let newWidth = ev.clientX - left;
      newWidth = Math.max(280, Math.min(640, newWidth));
      listWidthRef.current = newWidth;
      setListWidth(newWidth);
    };
    const onUp = () => {
      isResizingRef.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      localStorage.setItem("wa_list_width", String(listWidthRef.current));
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    
    if (isToday) {
      return date.toLocaleTimeString("es", { hour: "2-digit", minute: "2-digit" });
    }
    return date.toLocaleDateString("es", { day: "2-digit", month: "short" });
  };

  const formatFullTime = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    return date.toLocaleString("es", { 
      day: "2-digit", 
      month: "short",
      hour: "2-digit", 
      minute: "2-digit" 
    });
  };

  const filteredConversations = conversations.filter(conv => {
    if (soloReabiertas && !conv.reabierta) return false;
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      conv.contact_phone?.toLowerCase().includes(search) ||
      conv.contact_name?.toLowerCase().includes(search) ||
      conv.last_message?.toLowerCase().includes(search)
    );
  });

  const getInitials = (name, phone) => {
    if (name) {
      return name.split(" ").map(n => n[0]).join("").substring(0, 2).toUpperCase();
    }
    return phone?.slice(-2) || "??";
  };

  const openAssignDialog = (conversation) => {
    setAssignmentData({
      assigned_to: conversation?.assigned_to || "",
      department_id: conversation?.department_id || "",
      notes: ""
    });
    setAgentSearch("");
    setShowAssignDialog(true);
  };

  const handleAssign = async () => {
    if (!selectedConversation) return;
    
    if (!assignmentData.assigned_to && !assignmentData.department_id) {
      toast.error("Selecciona un agente o departamento");
      return;
    }

    try {
      setAssigning(true);
      await axios.post(`${API}/whatsapp/assign`, {
        conversation_id: selectedConversation.id,
        assigned_to: assignmentData.assigned_to || null,
        department_id: assignmentData.department_id || null,
        notes: assignmentData.notes || null
      }, getAuthHeaders());
      
      toast.success("Conversación asignada exitosamente");
      setShowAssignDialog(false);
      fetchConversations();
      
      // Update selected conversation
      setSelectedConversation(prev => ({
        ...prev,
        assigned_to: assignmentData.assigned_to,
        department_id: assignmentData.department_id,
        assigned_to_name: usuarios.find(u => u.id === assignmentData.assigned_to)?.name,
        department_name: departamentos.find(d => d.id === assignmentData.department_id)?.name
      }));
    } catch (err) {
      console.error("Error:", err);
      toast.error(err.response?.data?.detail || "Error al asignar conversación");
    } finally {
      setAssigning(false);
    }
  };

  // ===== CALL FUNCTIONS =====
  const handleCallClient = async () => {
    if (!selectedConversation?.contact_phone) return;
    
    // Clean the phone number
    let phone = selectedConversation.contact_phone.replace(/[\s\-\(\)]/g, "");
    
    // Log the call to backend
    try {
      await axios.post(`${API}/whatsapp/calls/log`, {
        conversation_id: selectedConversation.id,
        contact_phone: selectedConversation.contact_phone,
        contact_name: selectedConversation.contact_name || null,
        config_id: selectedConversation.config_id || null
      }, getAuthHeaders());
    } catch (err) {
      console.error("Error logging call:", err);
    }
    
    // Open tel: link
    window.open(`tel:${phone}`, "_self");
    toast.success(`Llamando a ${selectedConversation.contact_name || phone}...`);
  };

  const handleConcluir = async () => {
    if (!selectedConversation) return;
    if (!window.confirm("¿Concluir esta conversación? Se marcará como Concluida y se enviará la encuesta de satisfacción al cliente.")) return;
    setSendingEncuesta(true);
    try {
      const res = await axios.post(`${API}/whatsapp/conversations/${selectedConversation.id}/concluir`, {}, getAuthHeaders());
      toast.success(res.data?.encuesta_enviada
        ? "Conversación concluida ✓ y encuesta enviada al cliente"
        : "Conversación concluida ✓ (la encuesta está desactivada, no se envió)");
      const actualizada = { ...selectedConversation, concluida: true, reabierta: false };
      setSelectedConversation(actualizada);
      loadMessages(actualizada);
      fetchConversations();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al concluir la conversación");
    } finally {
      setSendingEncuesta(false);
    }
  };

  const fetchCallHistory = async (conversationId) => {
    try {
      setLoadingCallHistory(true);
      const res = await axios.get(`${API}/whatsapp/calls/history?conversation_id=${conversationId}&limit=20`, getAuthHeaders());
      setCallHistory(res.data?.calls || []);
    } catch (err) {
      console.error("Error fetching call history:", err);
      toast.error("Error al cargar historial de llamadas");
    } finally {
      setLoadingCallHistory(false);
    }
  };

  const openCallHistoryDialog = () => {
    if (selectedConversation) {
      fetchCallHistory(selectedConversation.id);
      setShowCallHistoryDialog(true);
    }
  };

  // Check if calls are enabled for the selected conversation's config
  const isCallsEnabledForConversation = () => {
    if (!selectedConversation || !configs.length) return false;
    const config = configs.find(c => c.id === selectedConversation.config_id);
    return config?.calls_enabled || false;
  };

  // Fetch WhatsApp contacts
  const fetchWaContacts = async (search = "") => {
    try {
      setLoadingContacts(true);
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      params.append("limit", "50");
      
      const res = await axios.get(`${API}/whatsapp/contacts?${params}`, getAuthHeaders());
      setWaContacts(res.data.contacts || []);
    } catch (err) {
      console.error("Error fetching contacts:", err);
    } finally {
      setLoadingContacts(false);
    }
  };

  // Open new conversation dialog prefilled from Contactos (?nuevo=telefono)
  const prefillNuevoRef = useRef(false);
  useEffect(() => {
    if (prefillNuevoRef.current || configs.length === 0) return;
    const params = new URLSearchParams(window.location.search);
    const nuevo = params.get("nuevo");
    if (nuevo) {
      prefillNuevoRef.current = true;
      setNewConvPhone(nuevo);
      setNewConvMessage("");
      setNewConvConfigId(configs[0]?.id || "");
      setSelectedContact(null);
      setNewConvTab("manual");
      setShowNewConversationDialog(true);
      window.history.replaceState({}, "", "/whatsapp/bandeja");
    }
  }, [configs]);

  // Open new conversation dialog
  const openNewConversationDialog = () => {
    setNewConvPhone("");
    setNewConvMessage("");
    setNewConvConfigId(configs[0]?.id || "");
    setSelectedContact(null);
    setContactSearch("");
    setSelectedColaborador(null);
    setColaboradorSearch("");
    setNewConvTab("manual");
    fetchWaContacts();
    setShowNewConversationDialog(true);
  };

  // Start new conversation
  const startNewConversation = async () => {
    if (!newConvMessage.trim()) {
      toast.error("Escriba un mensaje para iniciar la conversación");
      return;
    }

    // Chat interno entre colaboradores (no sale por WhatsApp)
    if (newConvTab === "colaboradores") {
      if (!selectedColaborador) {
        toast.error("Seleccione un colaborador");
        return;
      }
      try {
        setStartingConversation(true);
        await axios.post(`${API}/whatsapp/interna/iniciar`, {
          user_id: selectedColaborador.id,
          message_text: newConvMessage
        }, getAuthHeaders());
        toast.success(`Mensaje interno enviado a ${selectedColaborador.name || selectedColaborador.email}`);
        setShowNewConversationDialog(false);
        setTimeout(() => fetchConversations(), 800);
      } catch (err) {
        console.error("Error:", err);
        toast.error(err.response?.data?.detail || "Error al enviar mensaje interno");
      } finally {
        setStartingConversation(false);
      }
      return;
    }

    const phoneToUse = newConvTab === "contacts" && selectedContact 
      ? selectedContact.whatsapp 
      : newConvPhone;
    
    if (!phoneToUse) {
      toast.error("Ingrese un número de teléfono o seleccione un contacto");
      return;
    }
    
    if (!newConvConfigId) {
      toast.error("Seleccione una línea de WhatsApp");
      return;
    }

    try {
      setStartingConversation(true);
      
      // Clean phone number
      let cleanPhone = phoneToUse.replace(/[\s\-\(\)]/g, "");
      if (!cleanPhone.startsWith("+")) {
        cleanPhone = "+" + cleanPhone;
      }
      
      await axios.post(`${API}/whatsapp/send`, {
        config_id: newConvConfigId,
        recipient_phone: cleanPhone,
        message_text: newConvMessage
      }, getAuthHeaders());
      
      toast.success("Mensaje enviado. La conversación aparecerá en la lista.");
      setShowNewConversationDialog(false);
      
      // Refresh conversations
      setTimeout(() => {
        fetchConversations();
      }, 2000);
      
    } catch (err) {
      console.error("Error:", err);
      toast.error(err.response?.data?.detail || "Error al enviar mensaje");
    } finally {
      setStartingConversation(false);
    }
  };

  // Handle contact search
  useEffect(() => {
    if (showNewConversationDialog && newConvTab === "contacts") {
      const timer = setTimeout(() => {
        fetchWaContacts(contactSearch);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [contactSearch, showNewConversationDialog, newConvTab]);

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      
      <main className="flex-1 ml-0 md:ml-64 flex flex-col h-screen overflow-x-hidden pt-14 md:pt-0" data-testid="whatsapp-bandeja-page">
        {/* Stats Bar */}
        <div className="bg-white dark:bg-slate-800 border-b p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-green-500 flex items-center justify-center">
              <Inbox className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900 dark:text-white">Bandeja de Entrada</h1>
              <p className="text-sm text-slate-500">{stats.total_conversations || 0} conversaciones</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {isAdmin && (
              <div className="flex items-center gap-2 px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-sm" data-testid="stat-sin-asignar">
                <AlertCircle className="w-4 h-4" />
                {stats.unassigned_conversations || 0} sin asignar
              </div>
            )}
            {isAdmin && (
              <div className="flex items-center gap-2 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm" data-testid="stat-activas">
                <MessageCircle className="w-4 h-4" />
                {stats.active_conversations || 0} activas
              </div>
            )}
            <Button variant="outline" size="sm" onClick={handleManualRefresh} disabled={refreshing}>
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>
            <Button size="sm" onClick={openNewConversationDialog} className="bg-green-600 hover:bg-green-700">
              <MessageSquarePlus className="w-4 h-4 mr-2" />
              Nueva Conversación
            </Button>
          </div>
        </div>

        <div id="wa-bandeja-body" className="flex flex-1 overflow-hidden">
          {/* Conversations List */}
          <div
            className={`border-r bg-white dark:bg-slate-800 flex-col flex-shrink-0 max-lg:!w-full ${selectedConversation ? "hidden lg:flex" : "flex"}`}
            style={{ width: listWidth }}
          >
            {/* Search */}
            <div className="p-4 border-b space-y-2">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  placeholder="Buscar conversación..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSoloReabiertas(v => !v)}
                className={`w-full h-8 text-xs gap-1.5 ${soloReabiertas ? "bg-amber-500 hover:bg-amber-600 border-amber-500 text-white hover:text-white" : "text-amber-600 border-amber-300 hover:bg-amber-50 hover:text-amber-700"}`}
                data-testid="filtro-reabiertas-btn"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                {soloReabiertas ? "Viendo solo Reabiertas — quitar filtro" : "Reabiertas"}
                {reabiertasCount > 0 && (
                  <span
                    className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold leading-none ${soloReabiertas ? "bg-white text-amber-600" : "bg-amber-500 text-white"}`}
                    data-testid="reabiertas-count-badge"
                  >
                    {reabiertasCount}
                  </span>
                )}
              </Button>
              {filtrosOpts?.mostrar && (
                <>
                <div className="grid grid-cols-2 gap-1.5">
                <Select
                  value={filterEjecutivo || "all"}
                  onValueChange={(v) => setFilterEjecutivo(v === "all" ? "" : v)}
                >
                  <SelectTrigger className="h-8 text-xs" data-testid="filtro-ejecutivo-select">
                    <SelectValue placeholder="Ejecutivo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los ejecutivos</SelectItem>
                    {(filtrosOpts.ejecutivos || []).map(u => (
                      <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={filterDepartamento || "all"}
                  onValueChange={(v) => setFilterDepartamento(v === "all" ? "" : v)}
                >
                  <SelectTrigger className="h-8 text-xs" data-testid="filtro-departamento-select">
                    <SelectValue placeholder="Departamento" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los departamentos</SelectItem>
                    {(filtrosOpts.departamentos || []).map(d => (
                      <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                </div>
                {(filterEjecutivo || filterDepartamento) && (
                  <button
                    type="button"
                    onClick={() => { setFilterEjecutivo(""); setFilterDepartamento(""); }}
                    className="w-full text-[11px] text-blue-600 hover:underline text-left"
                    data-testid="limpiar-filtros-btn"
                  >
                    Limpiar filtros de ejecutivo/departamento
                  </button>
                )}
                </>
              )}
              {isAdmin && (
                <Button
                  variant={showHidden ? "default" : "outline"}
                  size="sm"
                  onClick={() => setShowHidden(v => !v)}
                  className="w-full h-8 text-xs gap-1.5"
                  data-testid="toggle-hidden-button"
                >
                  {showHidden ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                  {showHidden ? "Ocultando: viendo también las ocultas" : "Ver conversaciones ocultas"}
                </Button>
              )}
              {isAdmin && !selectionMode && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSelectionMode(true)}
                  className="w-full h-8 text-xs gap-1.5"
                  data-testid="select-mode-button"
                >
                  <CheckCheck className="w-3.5 h-3.5" />
                  Seleccionar para eliminar
                </Button>
              )}
              {isAdmin && selectionMode && (
                <div className="flex items-center gap-2" data-testid="selection-bar">
                  <span className="text-xs text-slate-600 dark:text-slate-300 flex-1">
                    {selectedIds.length} seleccionada(s)
                  </span>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={bulkDeleteConversations}
                    disabled={selectedIds.length === 0}
                    className="h-8 text-xs gap-1"
                    data-testid="bulk-delete-button"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    Eliminar
                  </Button>
                  <Button variant="outline" size="sm" onClick={exitSelectionMode} className="h-8 text-xs" data-testid="cancel-selection-button">
                    Cancelar
                  </Button>
                </div>
              )}
            </div>

            {/* Conversations */}
            <ScrollArea className="flex-1">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                </div>
              ) : filteredConversations.length === 0 ? (
                <div className="p-8 text-center text-slate-500">
                  <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No hay conversaciones</p>
                </div>
              ) : (
                filteredConversations.map((conv) => (
                  <div
                    key={conv.id}
                    onClick={() => selectionMode ? toggleSelect(conv.id) : loadMessages(conv)}
                    className={`p-4 border-b cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors ${
                      selectionMode && selectedIds.includes(conv.id) ? "bg-red-50 dark:bg-red-900/20" : selectedConversation?.id === conv.id ? "bg-green-50 dark:bg-green-900/20 border-l-4 border-l-green-500" : (conv.unread_count || 0) > 0 ? "bg-emerald-50/80 dark:bg-emerald-900/15 border-l-4 border-l-emerald-500" : ""
                    }`}
                    data-testid={`conversation-${conv.id}`}
                  >
                    <div className="flex items-start gap-3">
                      {selectionMode && (
                        <div className="pt-3" onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={selectedIds.includes(conv.id)}
                            onCheckedChange={() => toggleSelect(conv.id)}
                            data-testid={`select-conv-${conv.id}`}
                          />
                        </div>
                      )}
                      <Avatar className="w-12 h-12">
                        <AvatarFallback className="bg-green-100 text-green-700">
                          {getInitials(conv.contact_name, conv.contact_phone)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <span className={`truncate text-slate-900 dark:text-white ${(conv.unread_count || 0) > 0 ? "font-bold" : "font-semibold"}`} data-testid={`conv-name-${conv.id}`}>
                            {conv.contact_name || conv.contact_phone}
                            {(conv.unread_count || 0) > 0 && (
                              <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 ml-1.5 align-middle" data-testid={`conv-unread-dot-${conv.id}`} />
                            )}
                          </span>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <span className="text-xs text-slate-400">
                              {formatTime(conv.last_message_at)}
                            </span>
                            {isAdmin && (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                  <Button variant="ghost" size="icon" className="h-6 w-6" data-testid={`conv-menu-${conv.id}`}>
                                    <MoreVertical className="w-4 h-4 text-slate-400" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                                  <DropdownMenuItem onClick={(e) => markUnread(conv, e)} data-testid={`conv-unread-${conv.id}`}>
                                    <MessageCircle className="w-4 h-4 mr-2" />
                                    Marcar como no leída
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={(e) => hideConversation(conv, e)} data-testid={`conv-hide-${conv.id}`}>
                                    {conv.hidden ? <Eye className="w-4 h-4 mr-2" /> : <EyeOff className="w-4 h-4 mr-2" />}
                                    {conv.hidden ? "Mostrar" : "Ocultar"}
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem onClick={(e) => deleteConversation(conv, e)} className="text-red-600 focus:text-red-600" data-testid={`conv-delete-${conv.id}`}>
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Eliminar
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                          </div>
                        </div>
                        {conv.contact_name && (
                          <p className="text-xs text-slate-500">{conv.contact_phone}</p>
                        )}
                        <p className={`text-sm line-clamp-2 break-words mt-1 ${(conv.unread_count || 0) > 0 ? "font-semibold text-slate-800 dark:text-slate-200" : "text-slate-600 dark:text-slate-400"}`}>
                          {conv.last_message}
                        </p>
                        <div className="flex flex-wrap items-center gap-1.5 mt-2">
                          {conv.tipo === "interna" ? (
                            <Badge className="bg-indigo-500 hover:bg-indigo-600 text-white text-xs" data-testid={`conv-interna-badge-${conv.id}`}>
                              <Users className="w-3 h-3 mr-1" />
                              Interno
                            </Badge>
                          ) : (
                          <Badge variant="outline" className="text-xs">
                            {getConfigName(conv.config_id)}
                          </Badge>
                          )}
                          {conv.hidden && (
                            <Badge className="bg-slate-500 hover:bg-slate-600 text-xs" data-testid={`conv-hidden-badge-${conv.id}`}>
                              <EyeOff className="w-3 h-3 mr-1" />
                              Oculta
                            </Badge>
                          )}
                          {isWindowClosed(conv) && (
                            <Badge className="bg-orange-500 hover:bg-orange-600 text-xs" data-testid={`conv-window-badge-${conv.id}`} title="Pasaron 24h desde el último mensaje del cliente. Debes iniciar con una plantilla aprobada.">
                              <Clock className="w-3 h-3 mr-1" />
                              Usa plantilla (24h)
                            </Badge>
                          )}
                          {isAdmin && (conv.department_name || getDeptName(conv.department_id)) && (
                            <Badge className="bg-blue-500 hover:bg-blue-600 text-xs" data-testid={`conv-dept-badge-${conv.id}`}>
                              <Building2 className="w-3 h-3 mr-1" />
                              {conv.department_name || getDeptName(conv.department_id)}
                            </Badge>
                          )}
                          {conv.status === "new" && (
                            <Badge className="bg-amber-500 text-xs">Nuevo</Badge>
                          )}
                          {conv.unread_count > 0 && (
                            <Badge className="bg-green-500 text-xs">{conv.unread_count}</Badge>
                          )}
                          {(conv.assigned_to_name || getAgentName(conv.assigned_to)) && (
                            <Badge variant="secondary" className="text-xs" data-testid={`conv-agent-badge-${conv.id}`}>
                              <User className="w-3 h-3 mr-1" />
                              {conv.assigned_to_name || getAgentName(conv.assigned_to)}
                            </Badge>
                          )}
                          {!conv.assigned_to && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="h-6 px-2 text-xs text-amber-600 hover:text-amber-700"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedConversation(conv);
                                openAssignDialog(conv);
                              }}
                            >
                              <UserPlus className="w-3 h-3 mr-1" />
                              Asignar
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </ScrollArea>
          </div>

          {/* Resizer */}
          <div
            onMouseDown={startResize}
            className="w-1.5 cursor-col-resize bg-slate-200 dark:bg-slate-700 hover:bg-green-500 active:bg-green-600 transition-colors flex-shrink-0 hidden lg:block"
            title="Arrastra para ajustar el ancho"
            data-testid="bandeja-resizer"
          />

          {/* Chat Area */}
          <div
            className={`flex-1 flex-col bg-slate-100 dark:bg-slate-900 min-w-0 relative ${selectedConversation ? "flex" : "hidden lg:flex"}`}
            onDragOver={(e) => {
              if (selectedConversation && e.dataTransfer?.types?.includes("Files")) {
                e.preventDefault();
                setIsDraggingFile(true);
              }
            }}
            onDragLeave={(e) => {
              if (e.currentTarget === e.target || !e.currentTarget.contains(e.relatedTarget)) setIsDraggingFile(false);
            }}
            onDrop={handleFileDrop}
          >
            {isDraggingFile && selectedConversation && (
              <div className="absolute inset-0 z-40 bg-green-500/15 border-4 border-dashed border-green-500 rounded-lg flex items-center justify-center pointer-events-none" data-testid="drop-overlay">
                <div className="bg-white dark:bg-slate-800 px-6 py-4 rounded-xl shadow-lg text-center">
                  <Paperclip className="w-8 h-8 mx-auto mb-2 text-green-600" />
                  <p className="font-semibold text-green-700 dark:text-green-400">Suelta el archivo para enviarlo</p>
                  <p className="text-xs text-slate-500">a {selectedConversation?.contact_name || selectedConversation?.contact_phone}</p>
                </div>
              </div>
            )}
            {!selectedConversation ? (
              <div className="flex-1 flex items-center justify-center text-slate-500">
                <div className="text-center">
                  <MessageCircle className="w-16 h-16 mx-auto mb-4 opacity-30" />
                  <p className="text-lg">Selecciona una conversación</p>
                  <p className="text-sm">para ver los mensajes</p>
                </div>
              </div>
            ) : (
              <>
                {/* Chat Header */}
                <div className="bg-white dark:bg-slate-800 border-b p-2 sm:p-4 flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="lg:hidden flex-shrink-0 px-2"
                      onClick={() => setSelectedConversation(null)}
                      data-testid="volver-lista-btn"
                    >
                      <ArrowLeft className="w-4 h-4" />
                    </Button>
                    <Avatar className="w-10 h-10 flex-shrink-0">
                      <AvatarFallback className="bg-green-100 text-green-700">
                        {getInitials(selectedConversation.contact_name, selectedConversation.contact_phone)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0">
                      <h3 className="font-semibold text-slate-900 dark:text-white truncate">
                        {selectedConversation.contact_name || selectedConversation.contact_phone}
                      </h3>
                      <p className="text-sm text-slate-500 flex items-center gap-1">
                        <Phone className="w-3 h-3" />
                        {selectedConversation.contact_phone || (
                          <button
                            onClick={definirTelefono}
                            className="text-amber-600 underline hover:text-amber-700"
                            data-testid="agregar-telefono-btn"
                          >
                            Sin número — agregar
                          </button>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {selectedConversation.assigned_to_name && (
                      <Badge variant="outline">
                        <User className="w-3 h-3 mr-1" />
                        {selectedConversation.assigned_to_name}
                      </Badge>
                    )}
                    {selectedConversation.department_name && (
                      <Badge variant="secondary">
                        <Building2 className="w-3 h-3 mr-1" />
                        {selectedConversation.department_name}
                      </Badge>
                    )}
                    {isCallsEnabledForConversation() && (
                      <>
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="text-blue-600 hover:bg-blue-50 border-blue-300"
                          onClick={handleCallClient}
                          data-testid="call-client-btn"
                        >
                          <PhoneOutgoing className="w-4 h-4 mr-1" />
                          Llamar
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={openCallHistoryDialog}
                          data-testid="call-history-btn"
                          title="Historial de llamadas"
                        >
                          <History className="w-4 h-4 text-slate-500" />
                        </Button>
                      </>
                    )}
                    {selectedConversation.concluida ? (
                      <Badge className="bg-emerald-600 text-white" data-testid="badge-concluida">
                        <CheckCircle2 className="w-3 h-3 mr-1" />
                        Concluida
                      </Badge>
                    ) : (
                      <>
                      {selectedConversation.reabierta && (
                        <Badge className="bg-amber-500 text-white cursor-default" data-testid="badge-reabierta"
                          title={`Reabierta por ${selectedConversation.reabierta_por === "ejecutivo" ? "el ejecutivo" : "el cliente"}${selectedConversation.reabierta_por_nombre ? ` ${selectedConversation.reabierta_por_nombre}` : ""}${selectedConversation.reabierta_at ? ` el ${new Date(selectedConversation.reabierta_at).toLocaleString("es-DO")}` : ""}`}>
                          <RefreshCw className="w-3 h-3 mr-1" />
                          Reabierta
                        </Badge>
                      )}
                      {selectedConversation.almuerzo_activo && (
                        <Badge className="bg-orange-500 text-white cursor-default" data-testid="badge-almuerzo"
                          title={`Almuerzo activado por ${selectedConversation.almuerzo_por_nombre || ""}${selectedConversation.almuerzo_at ? ` el ${new Date(selectedConversation.almuerzo_at).toLocaleString("es-DO")}` : ""}`}>
                          🍽️ Almuerzo
                        </Badge>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        className={selectedConversation.almuerzo_activo ? "text-white bg-orange-500 hover:bg-orange-600 border-orange-500" : "text-orange-600 hover:bg-orange-50 border-orange-300"}
                        onClick={async () => {
                          try {
                            const nuevo = !selectedConversation.almuerzo_activo;
                            await axios.put(`${API}/whatsapp/conversations/${selectedConversation.id}/almuerzo`, { activo: nuevo }, getAuthHeaders());
                            setSelectedConversation({ ...selectedConversation, almuerzo_activo: nuevo });
                            toast.success(nuevo ? "Almuerzo activado para TODAS las conversaciones del ejecutivo (actuales y nuevas)" : "Almuerzo desactivado en todas las conversaciones: flujo normal");
                            fetchConversations();
                          } catch { toast.error("Error al cambiar el estado de almuerzo"); }
                        }}
                        data-testid="almuerzo-btn"
                        title="Aplica a todas las conversaciones del ejecutivo asignado, incluidas las que se le asignen mientras esté activo (mensaje configurable en Chatbot > Almuerzo)"
                      >
                        🍽️ Almuerzo
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="text-emerald-600 hover:bg-emerald-50 border-emerald-300"
                        onClick={handleConcluir}
                        disabled={sendingEncuesta}
                        data-testid="concluir-conversacion-btn"
                        title="Concluir la conversación y enviar la encuesta de satisfacción"
                      >
                        {sendingEncuesta ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
                        Concluir
                      </Button>
                      </>
                    )}
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => openAssignDialog(selectedConversation)}
                      data-testid="assign-conversation-btn"
                    >
                      <UserPlus className="w-4 h-4 mr-1" />
                      {selectedConversation.assigned_to ? "Reasignar" : "Asignar"}
                    </Button>
                  </div>
                </div>

                {/* Messages */}
                <ScrollArea className="flex-1 p-4">
                  {loadingMessages ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                    </div>
                  ) : messages.length === 0 ? (
                    <div className="text-center text-slate-500 py-12">
                      <p>No hay mensajes en esta conversación</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {messages.map((msg, idx) => (
                        <div
                          key={msg.id || idx}
                          className={`flex ${msg.direction === "outbound" ? "justify-end" : "justify-start"}`}
                        >
                          <div
                            className={`max-w-[85%] md:max-w-[600px] rounded-2xl px-4 py-2 ${
                              msg.direction === "outbound"
                                ? "bg-[#E8EEF4] dark:bg-slate-700 text-slate-800 dark:text-white border border-slate-200/80 dark:border-slate-600 rounded-br-md"
                                : "bg-white dark:bg-slate-800 text-slate-900 dark:text-white rounded-bl-md shadow-sm"
                            }`}
                          >
                            {["document", "image", "video", "audio"].includes(msg.message_type) ? (
                              msg.direction === "inbound" || msg.media_id || msg.interna_gridfs_id ? (
                                <InboundMediaMessage msg={msg} getAuthHeaders={getAuthHeaders} />
                              ) : (
                                <div className="flex items-center gap-2 py-1">
                                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${msg.direction === "outbound" ? "bg-[#0C314C] text-white" : "bg-slate-100 dark:bg-slate-700"}`}>
                                    <Paperclip className="w-4 h-4" />
                                  </div>
                                  <span className="flex flex-col">
                                    <span className="break-words font-medium">{msg.media_filename || msg.content}</span>
                                    {msg.media_size != null && <span className="text-xs opacity-70">{formatBytes(msg.media_size)}</span>}
                                  </span>
                                </div>
                              )
                            ) : (
                              <p className="whitespace-pre-wrap break-words">{msg.content}</p>
                            )}
                            <div className={`flex items-center justify-end gap-1 mt-1 text-xs ${
                              msg.direction === "outbound" ? "text-slate-500 dark:text-slate-400" : "text-slate-400"
                            }`}>
                              <span>{formatFullTime(msg.timestamp)}</span>
                              {msg.direction === "outbound" && (
                                msg.status === "read" ? (
                                  <CheckCheck className="w-3 h-3 text-blue-600 dark:text-blue-400" data-testid={`msg-check-read-${msg.id}`} />
                                ) : msg.status === "delivered" ? (
                                  <CheckCheck className="w-3 h-3" data-testid={`msg-check-delivered-${msg.id}`} />
                                ) : (
                                  <Check className="w-3 h-3" data-testid={`msg-check-sent-${msg.id}`} />
                                )
                              )}
                            </div>
                            {msg.sent_by_name && msg.direction === "outbound" && (
                              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                                Enviado por: {msg.sent_by_name}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                      <div ref={messagesEndRef} />
                    </div>
                  )}
                </ScrollArea>

                {/* Message Input */}
                <form onSubmit={handleSendMessage} className="p-2 sm:p-4 bg-white dark:bg-slate-800 border-t">
                  {isWindowClosed(selectedConversation) && (
                    <div className="mb-2 flex items-center gap-2 text-xs bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-900 text-orange-700 dark:text-orange-300 rounded-lg px-3 py-2" data-testid="window-closed-banner">
                      <Clock className="w-4 h-4 flex-shrink-0" />
                      <span>Pasaron más de 24h desde el último mensaje del cliente. Debes iniciar la conversación con una <strong>plantilla aprobada</strong>.</span>
                    </div>
                  )}
                  <div className="flex flex-wrap sm:flex-nowrap gap-1.5 sm:gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => openTemplateDialog({ configId: selectedConversation.config_id, phone: selectedConversation.contact_phone })}
                      className="self-end gap-1"
                      data-testid="open-template-button"
                      title="Enviar plantilla aprobada por Meta"
                    >
                      <FileText className="w-4 h-4" />
                      <span className="hidden sm:inline">Plantilla</span>
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          className="self-end gap-1 text-amber-600 border-amber-200 hover:bg-amber-50"
                          data-testid="quick-replies-button"
                          title="Respuestas rápidas"
                        >
                          <Zap className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start" className="w-80 max-h-72 overflow-y-auto">
                        {visibleQuickReplies.length === 0 ? (
                          <div className="px-3 py-2 text-sm text-slate-500">No hay respuestas rápidas configuradas</div>
                        ) : (
                          visibleQuickReplies.map((q) => (
                            <DropdownMenuItem
                              key={q.id}
                              data-testid={`quick-reply-item-${q.id}`}
                              onClick={() => setNewMessage((prev) => (prev.trim() ? `${prev} ${q.mensaje}` : q.mensaje))}
                              className="cursor-pointer"
                            >
                              <div className="min-w-0">
                                <p className="font-medium text-sm">{q.titulo}</p>
                                <p className="text-xs text-slate-500 truncate">{q.mensaje}</p>
                              </div>
                            </DropdownMenuItem>
                          ))
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                    <Textarea
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Escribe un mensaje... (puedes pegar una captura con Ctrl+V)"
                      className="flex-1 min-h-[44px] sm:min-h-[60px] max-h-32 resize-none basis-full sm:basis-auto order-first sm:order-none"
                      onPaste={handlePaste}
                      data-testid="message-input-textarea"
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          handleSendMessage(e);
                        }
                      }}
                    />
                    <Button 
                      type="submit" 
                      disabled={sending || !newMessage.trim()}
                      className="bg-green-500 hover:bg-green-600 self-end max-sm:flex-1"
                    >
                      {sending ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4" />
                      )}
                    </Button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      className="hidden"
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.jpg,.jpeg,.png,.gif,.tif,.tiff,.webp,.mp4,.zip,.rar,application/*,image/*"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) {
                          const url = (f.type || "").startsWith("image/") ? URL.createObjectURL(f) : null;
                          setPastedImage({ file: f, url });
                        }
                        e.target.value = "";
                      }}
                      data-testid="attachment-input"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      className="self-end"
                      disabled={uploadingMedia}
                      onClick={() => fileInputRef.current?.click()}
                      data-testid="attachment-button"
                      title="Adjuntar archivo (Word, Excel, PowerPoint, PDF, imágenes, etc.)"
                    >
                      {uploadingMedia ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
                    </Button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>

        {/* Assignment Dialog */}
        <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-green-500" />
                Asignar Conversación
              </DialogTitle>
              <DialogDescription>
                Asigna esta conversación a un agente o departamento
              </DialogDescription>
            </DialogHeader>

            {selectedConversation && (
              <div className="space-y-4">
                <div className="p-3 bg-slate-100 dark:bg-slate-800 rounded-lg">
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    <strong>Contacto:</strong> {selectedConversation.contact_name || selectedConversation.contact_phone}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    {selectedConversation.contact_phone}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Asignar a Agente
                  </Label>
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input
                      placeholder="Buscar agente por nombre o correo..."
                      value={agentSearch}
                      onChange={(e) => setAgentSearch(e.target.value)}
                      className="pl-10"
                      data-testid="assign-agent-search"
                    />
                  </div>
                  <Select 
                    value={assignmentData.assigned_to || "none"} 
                    onValueChange={(v) => setAssignmentData({...assignmentData, assigned_to: v === "none" ? "" : v})}
                  >
                    <SelectTrigger data-testid="assign-agent-select">
                      <SelectValue placeholder="Seleccionar agente..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin asignar</SelectItem>
                      {availableAgents.length === 0 ? (
                        <div className="px-3 py-2 text-sm text-slate-500">
                          {assignmentData.department_id
                            ? "No hay agentes en este departamento"
                            : "No hay agentes que coincidan"}
                        </div>
                      ) : (
                        availableAgents.map(u => (
                          <SelectItem key={u.id} value={u.id}>
                            {u.name || u.email}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  {assignmentData.department_id && (
                    <p className="text-xs text-slate-500">
                      Mostrando solo agentes del departamento seleccionado.
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    Asignar a Departamento
                  </Label>
                  <Select 
                    value={assignmentData.department_id || "none"} 
                    onValueChange={(v) => {
                      const newDept = v === "none" ? "" : v;
                      setAgentSearch("");
                      setAssignmentData(prev => {
                        const stillValid = prev.assigned_to && usuarios.find(u => u.id === prev.assigned_to && (!newDept || u.departamento_id === newDept));
                        return { ...prev, department_id: newDept, assigned_to: stillValid ? prev.assigned_to : "" };
                      });
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar departamento..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin departamento</SelectItem>
                      {departamentos.map(d => (
                        <SelectItem key={d.id} value={d.id}>
                          {d.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Notas (opcional)</Label>
                  <Textarea
                    value={assignmentData.notes}
                    onChange={(e) => setAssignmentData({...assignmentData, notes: e.target.value})}
                    placeholder="Agregar notas sobre esta asignación..."
                    rows={2}
                  />
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAssignDialog(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleAssign}
                disabled={assigning || (!assignmentData.assigned_to && !assignmentData.department_id)}
                className="bg-green-600 hover:bg-green-700"
              >
                {assigning && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Asignar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* New Conversation Dialog */}
        <Dialog open={showNewConversationDialog} onOpenChange={setShowNewConversationDialog}>
          <DialogContent className="max-w-[calc(100vw-1.5rem)] sm:max-w-xl md:max-w-2xl max-h-[92vh] overflow-y-auto overflow-x-hidden">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <MessageSquarePlus className="w-5 h-5 text-green-600" />
                Nueva Conversación
              </DialogTitle>
              <DialogDescription>
                Inicie una nueva conversación de WhatsApp
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              {/* WhatsApp Line Selection (no aplica para chat interno entre colaboradores) */}
              {newConvTab !== "colaboradores" && (
              <div>
                <Label>Línea de WhatsApp *</Label>
                <Select value={newConvConfigId} onValueChange={setNewConvConfigId}>
                  <SelectTrigger className="w-full">
                    <span className="truncate text-left">
                      <SelectValue placeholder="Seleccione una línea" />
                    </span>
                  </SelectTrigger>
                  <SelectContent>
                    {configs.map(config => (
                      <SelectItem key={config.id} value={config.id}>
                        {config.name} ({config.phone_number})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              )}

              {/* Tabs: Manual, Contacts o Colaboradores */}
              <Tabs value={newConvTab} onValueChange={setNewConvTab}>
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="manual" className="gap-1 px-1 text-xs sm:text-sm sm:gap-2" data-testid="new-conv-tab-manual">
                    <Phone className="w-4 h-4 shrink-0" />
                    <span className="truncate">Número Manual</span>
                  </TabsTrigger>
                  <TabsTrigger value="contacts" className="gap-1 px-1 text-xs sm:text-sm sm:gap-2" data-testid="new-conv-tab-contacts">
                    <Users className="w-4 h-4 shrink-0" />
                    <span className="truncate">Contactos</span>
                  </TabsTrigger>
                  <TabsTrigger value="colaboradores" className="gap-1 px-1 text-xs sm:text-sm sm:gap-2" data-testid="new-conv-tab-colaboradores">
                    <MessageSquarePlus className="w-4 h-4 shrink-0" />
                    <span className="truncate">Colaboradores</span>
                  </TabsTrigger>
                </TabsList>

                {/* Manual Phone Entry */}
                <TabsContent value="manual" className="space-y-4">
                  <div>
                    <Label>Número de WhatsApp *</Label>
                    <Input
                      value={newConvPhone}
                      onChange={(e) => setNewConvPhone(e.target.value)}
                      placeholder="+1809XXXXXXX"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Incluya el código de país (ej: +1809...)
                    </p>
                  </div>
                </TabsContent>

                {/* Select from Contacts */}
                <TabsContent value="contacts" className="space-y-4">
                  <div>
                    <Label>Buscar Contacto</Label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        value={contactSearch}
                        onChange={(e) => setContactSearch(e.target.value)}
                        placeholder="Buscar por nombre o número..."
                        className="pl-10"
                      />
                    </div>
                  </div>

                  <ScrollArea className="h-48 border rounded-lg">
                    {loadingContacts ? (
                      <div className="flex justify-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                      </div>
                    ) : waContacts.length === 0 ? (
                      <div className="text-center py-8 text-slate-500">
                        <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">No hay contactos</p>
                        <p className="text-xs">Importe contactos desde WhatsApp → Contactos</p>
                      </div>
                    ) : (
                      <div className="divide-y">
                        {waContacts.map(contact => (
                          <div
                            key={contact.id}
                            className={`p-3 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800 flex items-center justify-between ${
                              selectedContact?.id === contact.id ? "bg-green-50 dark:bg-green-900/20" : ""
                            }`}
                            onClick={() => setSelectedContact(contact)}
                          >
                            <div>
                              <p className="font-medium text-sm">
                                {contact.nombre} {contact.apellido}
                              </p>
                              <p className="text-xs text-slate-500">{contact.whatsapp}</p>
                            </div>
                            {selectedContact?.id === contact.id && (
                              <Check className="w-5 h-5 text-green-600" />
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </ScrollArea>

                  {selectedContact && (
                    <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg">
                      <p className="text-sm font-medium text-green-800 dark:text-green-200">
                        Contacto seleccionado: {selectedContact.nombre} {selectedContact.apellido}
                      </p>
                      <p className="text-xs text-green-600">{selectedContact.whatsapp}</p>
                    </div>
                  )}
                </TabsContent>

                {/* Chat interno con otro colaborador */}
                <TabsContent value="colaboradores" className="space-y-4">
                  <div>
                    <Label>Buscar Colaborador</Label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        value={colaboradorSearch}
                        onChange={(e) => setColaboradorSearch(e.target.value)}
                        placeholder="Buscar por nombre o correo..."
                        className="pl-10"
                        data-testid="colaborador-search-input"
                      />
                    </div>
                  </div>

                  <ScrollArea className="h-48 border rounded-lg" data-testid="colaboradores-list">
                    <div className="divide-y">
                      {usuarios
                        .filter(u => u.id !== user?.id)
                        .filter(u => !colaboradorSearch ||
                          `${u.name || ""} ${u.email || ""}`.toLowerCase().includes(colaboradorSearch.toLowerCase()))
                        .map(u => (
                          <div
                            key={u.id}
                            className={`p-3 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800 flex items-center justify-between ${
                              selectedColaborador?.id === u.id ? "bg-green-50 dark:bg-green-900/20" : ""
                            }`}
                            onClick={() => setSelectedColaborador(u)}
                            data-testid={`colaborador-item-${u.id}`}
                          >
                            <div className="min-w-0">
                              <p className="font-medium text-sm truncate">{u.name || u.email}</p>
                              <p className="text-xs text-slate-500 truncate">
                                {departamentos.find(d => d.id === u.departamento_id)?.name || u.email}
                              </p>
                            </div>
                            {selectedColaborador?.id === u.id && (
                              <Check className="w-5 h-5 text-green-600 shrink-0" />
                            )}
                          </div>
                        ))}
                    </div>
                  </ScrollArea>

                  {selectedColaborador && (
                    <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg">
                      <p className="text-sm font-medium text-green-800 dark:text-green-200">
                        Colaborador seleccionado: {selectedColaborador.name || selectedColaborador.email}
                      </p>
                      <p className="text-xs text-green-600">
                        Chat interno: la conversación se maneja desde esta misma bandeja, no sale por WhatsApp.
                      </p>
                    </div>
                  )}
                </TabsContent>
              </Tabs>

              {/* Message */}
              <div>
                <div className="flex items-center justify-between flex-wrap gap-1">
                  <Label>Mensaje Inicial *</Label>
                  {newConvTab !== "colaboradores" && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-green-700 hover:text-green-800 gap-1 h-7"
                    data-testid="new-conv-template-button"
                    onClick={() => {
                      const phone = newConvTab === "contacts" ? selectedContact?.whatsapp : newConvPhone;
                      if (!newConvConfigId) { toast.error("Seleccione una línea de WhatsApp"); return; }
                      if (!phone) { toast.error("Ingrese o seleccione un número de destino"); return; }
                      openTemplateDialog({ configId: newConvConfigId, phone });
                    }}
                  >
                    <FileText className="w-3.5 h-3.5" />
                    Usar plantilla aprobada
                  </Button>
                  )}
                </div>
                <Textarea
                  value={newConvMessage}
                  onChange={(e) => setNewConvMessage(e.target.value)}
                  placeholder="Escriba el primer mensaje..."
                  rows={3}
                />
                {newConvTab !== "colaboradores" && (
                <p className="text-xs text-amber-600 mt-1">
                  Para clientes que no le han escrito en las últimas 24h, debe usar una plantilla aprobada por Meta.
                </p>
                )}
              </div>
            </div>

            <DialogFooter className="flex-wrap gap-2">
              <Button variant="outline" onClick={() => setShowNewConversationDialog(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={startNewConversation}
                disabled={startingConversation || !newConvMessage.trim() || 
                  (newConvTab === "manual" && (!newConvConfigId || !newConvPhone)) || 
                  (newConvTab === "contacts" && (!newConvConfigId || !selectedContact)) ||
                  (newConvTab === "colaboradores" && !selectedColaborador)}
                className="bg-green-600 hover:bg-green-700"
                data-testid="new-conv-send-btn"
              >
                {startingConversation && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                <Send className="w-4 h-4 mr-2" />
                Enviar Mensaje
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog imagen pegada del portapapeles */}
        <Dialog open={!!pastedImage} onOpenChange={(v) => { if (!v && pastedImage) { if (pastedImage.url) URL.revokeObjectURL(pastedImage.url); setPastedImage(null); } }}>
          <DialogContent className="max-w-lg" data-testid="paste-image-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Paperclip className="w-5 h-5 text-green-600" />
                {pastedImage?.url ? "Enviar imagen" : "Enviar archivo"}
              </DialogTitle>
              <DialogDescription>
                Se enviará a {selectedConversation?.contact_name || selectedConversation?.contact_phone}.
              </DialogDescription>
            </DialogHeader>
            {pastedImage && (pastedImage.url ? (
              <img src={pastedImage.url} alt="vista previa" className="w-full max-h-[50vh] object-contain rounded-lg border" data-testid="paste-image-preview" />
            ) : (
              <div className="flex items-center gap-3 border rounded-lg p-4 bg-slate-50 dark:bg-slate-800/50" data-testid="paste-file-preview">
                <Paperclip className="w-8 h-8 text-slate-400 shrink-0" />
                <div className="min-w-0">
                  <p className="font-medium truncate dark:text-white">{pastedImage.file.name}</p>
                  <p className="text-xs text-slate-500">{formatBytes(pastedImage.file.size)}</p>
                </div>
              </div>
            ))}
            <DialogFooter>
              <Button variant="outline" onClick={() => { if (pastedImage.url) URL.revokeObjectURL(pastedImage.url); setPastedImage(null); }} data-testid="paste-image-cancel">Cancelar</Button>
              <Button onClick={sendPastedImage} disabled={uploadingMedia} className="bg-green-600 hover:bg-green-700" data-testid="paste-image-send">
                {uploadingMedia ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Enviar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Approved Template Dialog */}
        <Dialog open={showTemplateDialog} onOpenChange={setShowTemplateDialog}>
          <DialogContent className="max-w-lg" data-testid="template-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-green-600" />
                Enviar Plantilla Aprobada
              </DialogTitle>
              <DialogDescription>
                Seleccione una plantilla aprobada por Meta y complete sus variables.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  {templates.filter(t => t.status === "approved").length} plantilla(s) aprobada(s)
                </p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={syncTemplates}
                  disabled={syncingTemplates}
                  data-testid="sync-templates-button"
                  className="gap-1"
                >
                  {syncingTemplates ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                  Sincronizar desde Meta
                </Button>
              </div>

              <div>
                <Label>Plantilla</Label>
                <Select
                  value={selectedTemplate?.id || ""}
                  onValueChange={(id) => handleSelectTemplate(templates.find(t => t.id === id))}
                >
                  <SelectTrigger data-testid="template-select">
                    <SelectValue placeholder={loadingTemplates ? "Cargando..." : "Seleccione una plantilla"} />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.filter(t => t.status === "approved").length === 0 ? (
                      <div className="px-3 py-2 text-sm text-slate-500">
                        No hay plantillas aprobadas. Presione "Sincronizar desde Meta".
                      </div>
                    ) : (
                      templates.filter(t => t.status === "approved").map(tpl => (
                        <SelectItem key={tpl.id} value={tpl.id}>
                          {tpl.name} ({tpl.language}) · {tpl.category}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>

              {selectedTemplate && (
                <>
                  {templateVars.length > 0 && (
                    <div className="space-y-2">
                      <Label>Variables</Label>
                      {templateVars.map((v, i) => {
                        const hdrN = selectedTemplate.header_var_count || 0;
                        const varName = i >= hdrN ? (selectedTemplate.variable_names || [])[i - hdrN] : null;
                        return (
                          <Input
                            key={i}
                            value={v}
                            data-testid={`template-var-${i + 1}`}
                            placeholder={i < hdrN ? `Encabezado: valor para {{${i + 1}}}` : varName ? `Valor para {{${varName}}}` : `Valor para {{${hdrN ? i - hdrN + 1 : i + 1}}}`}
                            onChange={(e) => {
                              const next = [...templateVars];
                              next[i] = e.target.value;
                              setTemplateVars(next);
                            }}
                          />
                        );
                      })}
                      {templateVarsIncompletas && (
                        <div className="flex items-start gap-2 p-2.5 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-300 text-sm" data-testid="template-vars-warning">
                          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                          <span>Debe completar todas las variables antes de poder enviar la plantilla.</span>
                        </div>
                      )}
                    </div>
                  )}
                  <div>
                    <Label>Vista previa</Label>
                    <div className="mt-1 p-3 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-100 dark:border-green-900 text-sm whitespace-pre-wrap" data-testid="template-preview">
                      {renderTemplateText(selectedTemplate, templateVars)}
                    </div>
                  </div>
                </>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowTemplateDialog(false)}>Cancelar</Button>
              <Button
                onClick={sendTemplate}
                disabled={sendingTemplate || !selectedTemplate || (templateVars.length > 0 && templateVarsIncompletas)}
                data-testid="send-template-button"
                className="bg-green-600 hover:bg-green-700"
              >
                {sendingTemplate ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Enviar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Call History Dialog */}
        <Dialog open={showCallHistoryDialog} onOpenChange={setShowCallHistoryDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <PhoneCall className="w-5 h-5 text-blue-500" />
                Historial de Llamadas
              </DialogTitle>
              <DialogDescription>
                {selectedConversation?.contact_name || selectedConversation?.contact_phone}
              </DialogDescription>
            </DialogHeader>

            {loadingCallHistory ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : callHistory.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <PhoneCall className="w-10 h-10 mx-auto mb-3 opacity-30" />
                <p className="text-sm">No hay llamadas registradas</p>
              </div>
            ) : (
              <ScrollArea className="max-h-80">
                <div className="space-y-3">
                  {callHistory.map((call) => (
                    <div key={call.id} className="flex items-start gap-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg" data-testid={`call-record-${call.id}`}>
                      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                        <PhoneOutgoing className="w-4 h-4 text-blue-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 dark:text-white">
                          {call.called_by_name || "Usuario"}
                        </p>
                        <p className="text-xs text-slate-500">
                          {call.contact_phone}
                        </p>
                        <p className="text-xs text-slate-400 mt-1">
                          <Clock className="w-3 h-3 inline mr-1" />
                          {new Date(call.called_at).toLocaleString("es", { 
                            day: "2-digit", month: "short", year: "numeric",
                            hour: "2-digit", minute: "2-digit" 
                          })}
                        </p>
                        {call.notes && (
                          <p className="text-xs text-slate-600 mt-1 italic">{call.notes}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCallHistoryDialog(false)}>
                Cerrar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
